// ReadyFunnels.jsx

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Header from "units/dashboard/components/Header";
import { SidebarProvider } from "units/dashboard/context/SidebarProvider";
import AdminSidebar from "../components/AdminSidebar";
import { ProgressBar } from "react-loader-spinner";
import {
    PencilIcon,
    StarIcon,
    PlusIcon,
    EyeSlashIcon,
    ArrowPathIcon,
    TrashIcon,
    CheckIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
    getTemplates,
    setTemplateAsDefault,
    deactivateTemplate,
    reactivateTemplate,  // <-- اضافه شد
    deleteTemplate,
    getCriteria,
} from "./services/adminSellFunnel";
import EditTemplateModal from "./components/EditTemplateModal";
import { useNavigate } from "react-router-dom";

const ReadyFunnels = () => {
    const accessToken = useSelector((state) => state.auth?.accessToken);
    const navigate = useNavigate();
    // ============================
    // States
    // ============================
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // ============================
    // Action States
    // ============================
    const [actionLoading, setActionLoading] = useState(null);
    const [confirmationDialog, setConfirmationDialog] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: null,
        isDanger: false,
    });

    // ============================
    // Edit Modal States
    // ============================
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [criteriaData, setCriteriaData] = useState([]);

    // ============================
    // Load Criteria for dropdowns
    // ============================
    const loadCriteria = async () => {
        if (!accessToken) return;
        try {
            const response = await getCriteria(accessToken);
            setCriteriaData(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("LOAD CRITERIA ERROR:", err);
        }
    };

    // ============================
    // Load Templates
    // ============================
    const loadTemplates = async () => {
        if (!accessToken) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError("");
            const response = await getTemplates(accessToken);
            setTemplates(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("GET TEMPLATES ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "دریافت لیست قیف‌ها با خطا مواجه شد.";
            setError(backendMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
        loadCriteria();
    }, [accessToken]);

    // ============================
    // Get Criterion Title by ID
    // ============================
    const getCriterionTitle = (criterionId) => {
        if (!criterionId) return "بدون معیار";

        const criterion = criteriaData.find((c) => c.id === criterionId);
        return criterion?.title || "معیار حذف شده";
    };

    // ============================
    // Get Criterion Type by ID
    // ============================
    const getCriterionType = (criterionId) => {
        if (!criterionId) return null;

        const criterion = criteriaData.find((c) => c.id === criterionId);
        return criterion?.type || null;
    };

    // ============================
    // Get Type Badge for Criterion
    // ============================
    const getCriterionTypeBadge = (criterionId) => {
        const type = getCriterionType(criterionId);
        if (type === "AI") {
            return (
                <span className="inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                    AI
                </span>
            );
        }
        if (type === "SYSTEM_RULE") {
            return (
                <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    SYSTEM_RULE
                </span>
            );
        }
        return null;
    };

    // ============================
    // Open Edit Modal
    // ============================
    const handleEdit = (template) => {
        setEditingTemplate(template);
        setEditModalOpen(true);
    };

    // ============================
    // Close Edit Modal
    // ============================
    const handleEditModalClose = () => {
        setEditModalOpen(false);
        setEditingTemplate(null);
    };

    // ============================
    // After Save - Refresh List
    // ============================
    const handleEditSave = async () => {
        await loadTemplates();
    };

    // ============================
    // Set as Default
    // ============================
    const handleSetDefault = async (templateId) => {
        setConfirmationDialog({
            isOpen: true,
            title: "تنظیم به‌عنوان پیش‌فرض",
            message: "آیا از تنظیم این قیف به‌عنوان پیش‌فرض اطمینان دارید؟",
            onConfirm: async () => {
                setConfirmationDialog({ ...confirmationDialog, isOpen: false });
                try {
                    setActionLoading(templateId);
                    await setTemplateAsDefault(templateId, accessToken);
                    await loadTemplates();
                    setSuccessMessage("قیف با موفقیت به‌عنوان پیش‌فرض تنظیم شد.");
                    setTimeout(() => setSuccessMessage(""), 3000);
                } catch (err) {
                    console.error("SET DEFAULT ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        "تنظیم پیش‌فرض با خطا مواجه شد.";
                    setError(backendMessage);
                    setTimeout(() => setError(""), 5000);
                } finally {
                    setActionLoading(null);
                }
            },
            isDanger: false,
        });
    };

    // ============================
    // Deactivate Template (غیرفعال‌سازی)
    // ============================
    const handleDeactivate = async (template) => {
        if (template.isDefault) {
            setError("نمی‌توان قیف پیش‌فرض را غیرفعال کرد. ابتدا یک قیف دیگر را پیش‌فرض کنید.");
            setTimeout(() => setError(""), 5000);
            return;
        }

        setConfirmationDialog({
            isOpen: true,
            title: "غیرفعال‌سازی قیف",
            message: `آیا از غیرفعال‌سازی "${template.name}" اطمینان دارید؟`,
            onConfirm: async () => {
                setConfirmationDialog({ ...confirmationDialog, isOpen: false });
                try {
                    setActionLoading(template.id);
                    await deactivateTemplate(template.id, accessToken);
                    await loadTemplates();
                    setSuccessMessage("قیف با موفقیت غیرفعال شد.");
                    setTimeout(() => setSuccessMessage(""), 3000);
                } catch (err) {
                    console.error("DEACTIVATE ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        "غیرفعال‌سازی با خطا مواجه شد.";
                    setError(backendMessage);
                    setTimeout(() => setError(""), 5000);
                } finally {
                    setActionLoading(null);
                }
            },
            isDanger: true,
        });
    };

    // ============================
    // Reactivate Template (فعال‌سازی مجدد)
    // ============================
    const handleReactivate = async (template) => {
        setConfirmationDialog({
            isOpen: true,
            title: "فعال‌سازی مجدد قیف",
            message: `آیا از فعال‌سازی مجدد "${template.name}" اطمینان دارید؟`,
            onConfirm: async () => {
                setConfirmationDialog({ ...confirmationDialog, isOpen: false });
                try {
                    setActionLoading(template.id);
                    await reactivateTemplate(template.id, accessToken);
                    await loadTemplates();
                    setSuccessMessage("قیف با موفقیت فعال شد.");
                    setTimeout(() => setSuccessMessage(""), 3000);
                } catch (err) {
                    console.error("REACTIVATE ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        "فعال‌سازی مجدد با خطا مواجه شد.";
                    setError(backendMessage);
                    setTimeout(() => setError(""), 5000);
                } finally {
                    setActionLoading(null);
                }
            },
            isDanger: false,
        });
    };

    // ============================
    // Hard Delete
    // ============================
    const handleDelete = async (template) => {
        if (template.isDefault) {
            setError("نمی‌توان قیف پیش‌فرض را حذف کرد. ابتدا یک قیف دیگر را پیش‌فرض کنید.");
            setTimeout(() => setError(""), 5000);
            return;
        }

        if (template.isActive) {
            setError("نمی‌توان قیف فعال را حذف کرد. ابتدا آن را غیرفعال کنید.");
            setTimeout(() => setError(""), 5000);
            return;
        }

        setConfirmationDialog({
            isOpen: true,
            title: "حذف قطعی قیف",
            message: `آیا از حذف "${template.name}" اطمینان دارید؟ این عمل قابل برگشت نیست.`,
            onConfirm: async () => {
                setConfirmationDialog({ ...confirmationDialog, isOpen: false });
                try {
                    setActionLoading(template.id);
                    await deleteTemplate(template.id, accessToken);
                    await loadTemplates();
                    setSuccessMessage("قیف با موفقیت حذف شد.");
                    setTimeout(() => setSuccessMessage(""), 3000);
                } catch (err) {
                    console.error("DELETE ERROR:", err);
                    const backendMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.detail ||
                        "حذف قیف با خطا مواجه شد.";
                    setError(backendMessage);
                    setTimeout(() => setError(""), 5000);
                } finally {
                    setActionLoading(null);
                }
            },
            isDanger: true,
        });
    };

    // ============================
    // Navigate to Create Page
    // ============================
    const handleCreateNew = () => {
        navigate("/admin/create-ready-funnel");
    };

    // ============================
    // Render Functions
    // ============================
    const getStageNumber = (stageOrder) => {
        const stageMap = {
            1: "۱",
            2: "۲",
            3: "۳",
            4: "۴",
            5: "۵",
            6: "۶",
        };
        return stageMap[stageOrder] || stageOrder;
    };

    const getStatusBadge = (isActive, isDefault) => {
        if (isDefault) {
            return (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <StarIcon className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    پیش‌فرض
                </span>
            );
        }
        if (!isActive) {
            return (
                <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400">
                    <EyeSlashIcon className="h-3.5 w-3.5" />
                    غیرفعال
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                <CheckIcon className="h-3.5 w-3.5" />
                فعال
            </span>
        );
    };

    // ============================
    // Confirmation Dialog Component
    // ============================
    const ConfirmationDialog = () => {
        if (!confirmationDialog.isOpen) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl dark:bg-zinc-800">
                    <div className="p-6">
                        <div className="flex items-center gap-3">
                            <div className={`rounded-full p-2 ${confirmationDialog.isDanger ? "bg-red-100 dark:bg-red-900/30" : "bg-amber-100 dark:bg-amber-900/30"}`}>
                                <ExclamationTriangleIcon className={confirmationDialog.isDanger ? "h-6 w-6 text-red-600 dark:text-red-400" : "h-6 w-6 text-amber-600 dark:text-amber-400"} />
                            </div>
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white">
                                {confirmationDialog.title}
                            </h3>
                        </div>

                        <p className="mt-4 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                            {confirmationDialog.message}
                        </p>

                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => setConfirmationDialog({ ...confirmationDialog, isOpen: false })}
                                className="flex-1 rounded-lg border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-700/50"
                            >
                                انصراف
                            </button>
                            <button
                                onClick={confirmationDialog.onConfirm}
                                className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition ${confirmationDialog.isDanger
                                    ? "bg-red-600 hover:bg-red-700"
                                    : "bg-teal-700 hover:bg-teal-800"
                                    }`}
                            >
                                تأیید
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ============================
    // Main Render
    // ============================
    return (
        <SidebarProvider>
            {/* Header */}
            <nav className="fixed left-0 right-0 top-0 z-40 flex items-center border-b border-teal-100 bg-white print:hidden dark:border-zinc-700 dark:bg-zinc-800">
                <Header />
            </nav>

            {/* Sidebar */}
            <div className="vertical-menu rtl:right-0 ltr:left-0 fixed bottom-0 top-16 z-30 h-[calc(100vh-4rem)] border-r border-teal-100 bg-white print:hidden dark:border-neutral-700 dark:bg-zinc-800">
                <AdminSidebar />
            </div>

            {/* Main */}
            <main dir="rtl" className="min-h-screen overflow-x-hidden bg-[#F8FAFA] px-4 pb-8 pt-20 sm:px-6 lg:mr-[250px] lg:px-8">
                {loading ? (
                    <div className="flex min-h-[calc(100vh-5rem)] items-center justify-center">
                        <ProgressBar visible={true} height="80" width="80" color="#0F766E" />
                    </div>
                ) : (
                    <div>
                        {/* Page Header */}
                        <div className="mb-6">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">
                                        قیف‌های آماده
                                    </h1>
                                    <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                                        هر قیف یک قالب کامل با ۶ مرحله است. وقتی مدیر کسب‌وکار
                                        یکی را انتخاب می‌کند، این تنظیمات <strong>کپی</strong> می‌شود؛
                                        تغییر بعدی روی کسب‌وکارهای قبلی اثر نمی‌گذارد.
                                    </p>
                                </div>
                                <button
                                    onClick={handleCreateNew}
                                    className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-700"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    ایجاد قیف جدید
                                </button>
                            </div>
                        </div>

                        {/* Error/Success Messages */}
                        {error && (
                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/30 dark:bg-red-900/20 dark:text-red-400">
                                {error}
                            </div>
                        )}
                        {successMessage && (
                            <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700 dark:border-teal-800/30 dark:bg-teal-900/20 dark:text-teal-400">
                                {successMessage}
                            </div>
                        )}

                        {/* Empty State */}
                        {templates.length === 0 && !error && (
                            <div className="rounded-2xl border border-dashed border-teal-200 bg-white p-12 text-center dark:border-zinc-700 dark:bg-zinc-800">
                                <div className="mx-auto max-w-sm">
                                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 dark:bg-teal-900/30">
                                        <PlusIcon className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                        هیچ قیف آماده‌ای ایجاد نشده است
                                    </h3>
                                    <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                                        اولین قیف آماده خود را ایجاد کنید تا مدیران کسب‌وکار
                                        بتوانند از آن به‌عنوان الگو استفاده کنند.
                                    </p>
                                    <button
                                        onClick={handleCreateNew}
                                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-700"
                                    >
                                        <PlusIcon className="h-5 w-5" />
                                        ایجاد قیف جدید
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Templates List */}
                        {templates.length > 0 && (
                            <div className="space-y-4">
                                {templates.map((template) => (
                                    <div
                                        key={template.id}
                                        className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-zinc-800 ${!template.isActive
                                            ? "border-zinc-200 opacity-70 dark:border-zinc-700"
                                            : "border-teal-100 dark:border-zinc-700"
                                            }`}
                                    >
                                        {/* Template Header */}
                                        <div className="flex flex-wrap items-center justify-between border-b border-teal-50 p-4 dark:border-zinc-700">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                                    {template.name}
                                                </h2>
                                                {getStatusBadge(template.isActive, template.isDefault)}
                                                {template.businessCategory && (
                                                    <span className="rounded-full bg-teal-50 px-3 py-1 text-xs text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                                                        {template.businessCategory}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-2 flex flex-wrap items-center gap-2 sm:mt-0">
                                                {!template.isDefault && template.isActive && (
                                                    <button
                                                        onClick={() => handleSetDefault(template.id)}
                                                        disabled={actionLoading === template.id}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-teal-200 px-3 py-1.5 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-50 dark:border-teal-700 dark:text-teal-400 dark:hover:bg-teal-900/30"
                                                    >
                                                        <CheckIcon className="h-4 w-4" />
                                                        تنظیم پیش‌فرض
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(template)}
                                                    className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-700/50"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                    ویرایش
                                                </button>

                                                {/* دکمه غیرفعال‌سازی */}
                                                {template.isActive && (
                                                    <button
                                                        onClick={() => handleDeactivate(template)}
                                                        disabled={actionLoading === template.id}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-amber-200 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30"
                                                    >
                                                        <EyeSlashIcon className="h-4 w-4" />
                                                        غیرفعال‌سازی
                                                    </button>
                                                )}

                                                {/* دکمه فعال‌سازی مجدد */}
                                                {!template.isActive && (
                                                    <button
                                                        onClick={() => handleReactivate(template)}
                                                        disabled={actionLoading === template.id}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-teal-200 px-3 py-1.5 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-50 dark:border-teal-700 dark:text-teal-400 dark:hover:bg-teal-900/30"
                                                    >
                                                        <ArrowPathIcon className="h-4 w-4" />
                                                        فعال‌سازی مجدد
                                                    </button>
                                                )}

                                                {/* دکمه حذف کامل */}
                                                {!template.isActive && !template.isDefault && (
                                                    <button
                                                        onClick={() => handleDelete(template)}
                                                        disabled={actionLoading === template.id}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                        حذف کامل
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stages */}
                                        <div className="p-4">
                                            <div className="divide-y divide-teal-100 dark:divide-zinc-700">
                                                {template.stages?.map((stage) => {
                                                    const criterionTitle = getCriterionTitle(stage.criterionId);
                                                    const criterionTypeBadge = getCriterionTypeBadge(stage.criterionId);

                                                    return (
                                                        <div
                                                            key={stage.stageOrder}
                                                            className="flex flex-wrap items-center gap-3 py-2.5"
                                                        >
                                                            {/* Stage Number */}
                                                            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-xs font-bold text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                                                                {getStageNumber(stage.stageOrder)}
                                                            </div>

                                                            {/* Stage Name */}
                                                            <span className="min-w-[80px] font-medium text-zinc-800 dark:text-zinc-200 sm:min-w-[110px]">
                                                                {stage.stageName || `مرحله ${stage.stageOrder}`} :
                                                            </span>

                                                            {/* Criterion Name */}
                                                            <span className="text-sm text-zinc-500 dark:text-zinc-400">
                                                                {criterionTitle}
                                                            </span>

                                                            {/* Criterion Type Badge */}
                                                            {criterionTypeBadge && (
                                                                <span className="mr-1">
                                                                    {criterionTypeBadge}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Edit Template Modal */}
            <EditTemplateModal
                isOpen={editModalOpen}
                onClose={handleEditModalClose}
                template={editingTemplate}
                criteriaData={criteriaData}
                onSave={handleEditSave}
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog />
        </SidebarProvider>
    );
};

export default ReadyFunnels;